import React, {
  useContext, useEffect, useMemo, useState,
} from "react";

import { Button, Drawer } from "antd";
import { toast } from "react-toastify";

import useAbortController from "../../../Common/hooks/useAbortController";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import SampleSubmissionContext from "../../SampleSubmissionContext";
import SampleSubmissionConfirmModal from "../../SampleSubmissionModal/SampleSubmissionConfirmModal";
import DraftSubmissionCard from "./DraftSubmissionCard";

import { deleteFileFromS3WithPresigned } from "../../../../utils/helpers";

import {
  getDraftSubmissionsInfo,
  deleteDraft,
} from "../../../../actions/sampleSubmission";

import { ReactComponent as ChevronDown } from "../../../../assets/images/common/ChevronDown.svg";

import "../../SampleSubmission.css";
import "../MainPage.css";
import "./Drafts.css";

function isDraftScheduled(submission) {
  return submission.type === "scheduled";
}

const anyDrafts = (result) => Array.isArray(result) && result.length > 0;

export default function Drafts({
  setShowSubmissionForm,
  setDraftEditing,
  loadingDrafts,
  setLoadingDrafts,
  draftSubmissions,
  setDraftSubmissions,
  isDraftDrawerOpen,
  closeDraftDrawer,
  setHasDraftsGlobally,
}) {
  const { sampleCategory } = useContext(SampleSubmissionContext);

  const [draftToDelete, setDraftToDelete] = useState(-1);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(true);
  const [draftScheduleTab] = useState("non_scheduled");
  const {
    setNewAbortController: setNewListAbortController,
    clearAbortController: clearListAbortController,
  } = useAbortController();
  const {
    setNewAbortController,
    clearAbortController,
  } = useAbortController();

  const visibleDrafts = useMemo(
    () => draftSubmissions.filter((s) => (draftScheduleTab === "scheduled"
      ? isDraftScheduled(s)
      : !isDraftScheduled(s))),
    [draftSubmissions, draftScheduleTab],
  );

  /** Get list of draft submissions
  * @param {String} submissionId - the unique id of a submission
  * @param {String} type one of ""/"scheduled"/"non_scheduled" to filter drafts on the backend
  */
  const apiGetDraftSubmissionsInfo = async (submissionId = "", type = "") => {
    setLoadingDrafts(true);
    const newAbortController = setNewListAbortController();
    const {
      success: draftSuccess, message: draftMessage,
      result: draft_submissions,
    } = await getDraftSubmissionsInfo(submissionId, newAbortController.signal, type);

    if (newAbortController.signal.aborted) {
      return;
    }

    if (draftSuccess) {
      setDraftSubmissions(draft_submissions);
      if (submissionId === "") {
        setHasDraftsGlobally(anyDrafts(draft_submissions));
      }
    } else {
      toast.error(draftMessage);
    }

    clearListAbortController();
    setLoadingDrafts(false);
  };

  /** on component mount - get submitted submissions and submission drafts lists */
  useEffect(() => {
    apiGetDraftSubmissionsInfo("", "non_scheduled");
    setLoadingFields(false);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isDraftDrawerOpen) {
      apiGetDraftSubmissionsInfo("", draftScheduleTab);
    }
  }, [isDraftDrawerOpen, draftScheduleTab]); // eslint-disable-line

  /**
   * Delete draft
   * @param  {Number} idx index of draft being deleted in draftSubmissions list
   */
  const handleConfirmDeleteDraft = async (idx) => {
    /** If env, get draft's samples so we can loop over them and delete images */
    let samples;
    setDeleteLoading(true);
    if (sampleCategory === "environment") {
      const { success, result } = await getDraftSubmissionsInfo(draftSubmissions[idx].submission_id);
      if (success) {
        samples = result.samples;
      }
    }
    const { success, message } = await deleteDraft(draftSubmissions[idx].submission_id, draftSubmissions[idx].sample_category);
    if (success) {
      toast.success("Successfully deleted draft");
      draftSubmissions.splice(idx, 1);
      setDraftSubmissions([...draftSubmissions]);
      setDraftToDelete(-1);
      const { success: gOk, result: allDrafts } = await getDraftSubmissionsInfo("", null, "");
      if (gOk) setHasDraftsGlobally(anyDrafts(allDrafts));
      /** If a sample has an image_path, delete image from aws */
      if (samples && samples.length > 0) {
        samples.forEach(({ image_path }) => {
          if (image_path) {
            deleteFileFromS3WithPresigned([image_path], "private");
          }
        });
      }
    } else {
      toast.error(message);
    }
    setDeleteLoading(false);
  };

  /**
   * Handle delete draft click
   * @param  {Number} idx index of draft being deleted in draftSubmissions list
   */
  const handleDeleteDraftClick = async (idx) => {
    setDraftToDelete(idx);
  };

  const drawerStyles = {
    header: {
      display: "none",
    },
    body: {
      padding: 0,
    },
    content: {
      marginTop: 60,
      height: "calc(100vh - 60px)",
      borderBottomLeftRadius: "8px",
      borderTopLeftRadius: "8px",
      overflow: "hidden",
    },
    wrapper: {
      boxShadow: "none",
    },
  };

  return (
    <Drawer
      open={isDraftDrawerOpen}
      onClose={closeDraftDrawer}
      styles={drawerStyles}
    >
      {(draftToDelete >= 0) // delete draft confirmation modal
        && (
          <SampleSubmissionConfirmModal
            headerText="Delete Draft Confirmation"
            bodyText="Are you sure you want to delete this draft? This cannot be undone."
            buttonText={["Cancel", "Delete", "Deleting"]}
            buttonFunctions={[() => { setDraftToDelete(-1); }, () => handleConfirmDeleteDraft(draftToDelete)]}
            loading={deleteLoading}
          />
        )}
      <div className="draft-submission-container">
        <div className="draft-submission-header">
          <div className="draft-submission-header__left">
            <Button
              type="text"
              htmlType="button"
              className={`Draft_DrawerToggle ${isDraftDrawerOpen ? "Draft_DrawerToggle--open" : ""}`}
              onClick={closeDraftDrawer}
              aria-label="Close drafts"
            >
              <ChevronDown className="Draft_DrawerToggleIcon Draft_DrawerToggleIcon--back" />
            </Button>
            <h2 className="draft-submission-header__title">Drafts</h2>
          </div>
          {/* <div className="draft-submission-header__segments" role="tablist" aria-label="Draft schedule filter">
            <Button
              type="text"
              htmlType="button"
              role="tab"
              aria-selected={draftScheduleTab === "non_scheduled"}
              className={`draft-submission-header__segment ${draftScheduleTab === "non_scheduled" ? "draft-submission-header__segment--active" : ""}`}
              onClick={() => setDraftScheduleTab("non_scheduled")}
            >
              Non Scheduled
            </Button>
            <Button
              type="text"
              htmlType="button"
              role="tab"
              aria-selected={draftScheduleTab === "scheduled"}
              className={`draft-submission-header__segment ${draftScheduleTab === "scheduled" ? "draft-submission-header__segment--active" : ""}`}
              onClick={() => setDraftScheduleTab("scheduled")}
            >
              Scheduled
            </Button>
          </div> */}
        </div>
        <div className="draft-submission-background">
          <StyledScrollbar>
            <div className="draft-submission-scroll-inner">
              {(loadingFields || loadingDrafts || draftSubmissions.length > 0) && (loadingFields || loadingDrafts || visibleDrafts.length > 0) && (
                <ul className="draft-submissions-ul">
                  {!(loadingFields || loadingDrafts) && visibleDrafts.map((submission, idx) => {
                    const originalIndex = draftSubmissions.findIndex(
                      (d) => d.submission_id === submission.submission_id,
                    );
                    return (
                      <DraftSubmissionCard
                        key={submission.submission_id ?? idx}
                        displayIndex={idx + 1}
                        originalIndex={originalIndex}
                        submission={submission}
                        setDraftEditing={setDraftEditing}
                        setShowSubmissionForm={setShowSubmissionForm}
                        handleDeleteDraft={handleDeleteDraftClick}
                        setNewAbortController={setNewAbortController}
                        clearAbortController={clearAbortController}
                      />
                    );
                  })}
                  {(loadingFields || loadingDrafts) && (<div className="submitted-submissions-table-skeleton--draft ActiveloadingAnimation" />)}
                </ul>
              )}
              {!loadingFields && !loadingDrafts && draftSubmissions.length === 0 && (
                <div className="submitted-submissions-no-results--draft">No drafts data</div>
              )}
              {!loadingFields && !loadingDrafts && draftSubmissions.length > 0 && visibleDrafts.length === 0 && (
                <div className="submitted-submissions-no-results--draft">
                  {draftScheduleTab === "scheduled" ? "No scheduled drafts" : "No non-scheduled drafts"}
                </div>
              )}
            </div>
          </StyledScrollbar>
        </div>
      </div>
    </Drawer>
  );
}
